// src/buyAdvisorLogic.js

import { summarizeHistory } from "./supabase";

export const MIN_ROI_PCT = 20;
export const MAX_PCT_OF_MARKET = 85;
export const MIN_SALES_HIGH_CONFIDENCE = 8;
export const MIN_SALES_MEDIUM_CONFIDENCE = 3;
export const HIGH_SPREAD_PCT = 40;
export const CROSS_MARKET_DIVERGENCE_PCT = 25;

export function getConfidenceLevel(saleCount) {
  if (!saleCount) return "none";
  if (saleCount >= MIN_SALES_HIGH_CONFIDENCE) return "high";
  if (saleCount >= MIN_SALES_MEDIUM_CONFIDENCE) return "medium";
  return "low";
}

export function computeSpreadPct(summary) {
  if (!summary || !summary.center) return null;
  return Math.round(((summary.high - summary.low) / summary.center) * 1000) / 10;
}

export function computeTrend(summary7, summary30) {
  if (!summary7?.center || !summary30?.center) return { direction: "unknown", pct: null };
  const pct = Math.round(((summary7.center - summary30.center) / summary30.center) * 1000) / 10;
  const direction = pct > 5 ? "rising" : pct < -5 ? "falling" : "stable";
  return { direction, pct };
}

export function computeCrossMarketDivergence(pkmnpricesCenter, apitcgMarketPrice) {
  if (!pkmnpricesCenter || !apitcgMarketPrice) return null;
  return Math.round(((pkmnpricesCenter - apitcgMarketPrice) / apitcgMarketPrice) * 1000) / 10;
}

export function evaluateBuyDecision({ rows7d, rows30d, condition, variant, apitcgMarketPrice, dealerPrice }) {
  const summary7 = summarizeHistory(rows7d, condition, variant);
  const summary30 = summarizeHistory(rows30d, condition, variant);

  const useSummary = summary7 && summary7.saleCount >= MIN_SALES_MEDIUM_CONFIDENCE ? summary7 : summary30;
  const saleCount = useSummary?.saleCount || 0;
  const confidence = getConfidenceLevel(saleCount);
  const spreadPct = computeSpreadPct(useSummary);
  const trend = computeTrend(summary7, summary30);
  const crossDivergence = computeCrossMarketDivergence(useSummary?.center, apitcgMarketPrice);

  const reference = useSummary?.center || apitcgMarketPrice || null;
  const dealer = parseFloat(dealerPrice) || 0;

  const metrics = {
    saleCount7d: summary7?.saleCount || 0,
    saleCount30d: summary30?.saleCount || 0,
    confidence, spreadPct, trend, crossDivergence, reference,
  };

  if (!reference) {
    return { verdict: "insufficient_data", confidence, reasons: ["No sale history or market price available"], metrics };
  }
  if (!dealer) {
    return { verdict: "enter_price", confidence, reasons: [`Reference price: $${reference.toFixed(2)}`], metrics };
  }

  const projROI = ((reference - dealer) / dealer) * 100;
  const pctOfMarket = (dealer / reference) * 100;
  const roiOk = projROI >= MIN_ROI_PCT;
  const pctOk = pctOfMarket <= MAX_PCT_OF_MARKET;

  let verdict = roiOk && pctOk ? "buy" : roiOk ? "caution" : "pass";

  const reasons = [];
  reasons.push(`${projROI.toFixed(0)}% projected ROI (min ${MIN_ROI_PCT}%)`);
  if (!pctOk) reasons.push(`${pctOfMarket.toFixed(0)}% of market — above ${MAX_PCT_OF_MARKET}% threshold`);
  reasons.push(`${saleCount} sale${saleCount === 1 ? "" : "s"} in last ${summary7 && summary7.saleCount >= MIN_SALES_MEDIUM_CONFIDENCE ? "7" : "30"} days (${confidence} confidence)`);
  if (spreadPct !== null && spreadPct > HIGH_SPREAD_PCT) reasons.push(`Wide price spread (${spreadPct.toFixed(0)}%) — sales range varies significantly`);
  if (trend.direction !== "unknown") reasons.push(`Price trend: ${trend.direction}${trend.pct !== null ? ` ${trend.pct > 0 ? "+" : ""}${trend.pct.toFixed(0)}%` : ""}`);
  if (crossDivergence !== null && Math.abs(crossDivergence) > CROSS_MARKET_DIVERGENCE_PCT) {
    reasons.push(`TCGplayer market price diverges ${crossDivergence.toFixed(0)}% from eBay average`);
  }

  if (verdict === "buy" && (confidence === "low" || confidence === "none")) verdict = "caution";
  if (verdict === "buy" && spreadPct !== null && spreadPct > HIGH_SPREAD_PCT) verdict = "caution";
  if (verdict === "buy" && crossDivergence !== null && Math.abs(crossDivergence) > CROSS_MARKET_DIVERGENCE_PCT) verdict = "caution";

  return { verdict, confidence, reasons, metrics };
}
